import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle, CheckCircle, ChevronDown, ChevronRight, DollarSign,
  Loader2, MoreVertical, Package, Pencil, Save, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { REPAIR_TYPES, ACTION_TYPES } from './constants';
import SaveToMasterModal from './SaveToMasterModal';

// Repair Item Component
// Enhanced RepairItem Component - Synced with Repairs Module
const RepairItem = ({ 
  repair, 
  index, 
  onUpdate, 
  onRemove,
  onSaveToMaster,
  repairParts = [], 
  partCategories = [],
  vehicleMake = '', 
  carType = 'sedan',
  canEdit = true,
  countryId = 'c49e1dc6-1450-40c2-9846-56b73369b2b1'
}) => {
  const [showPartSelector, setShowPartSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBrandPricing, setShowBrandPricing] = useState(false);
  const [showSaveToMasterModal, setShowSaveToMasterModal] = useState(false);
  
  // Q&A linking state
  const [qaCategories, setQaCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [selectedQuestionData, setSelectedQuestionData] = useState(null);
  
  // Load Q&A categories on mount
  useEffect(() => {
    loadQaCategories();
  }, []);
  
  // Load Q&A categories
  const loadQaCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-qa/questions`);
      const cats = [...new Set((response.data || []).map(q => q.category_name))].filter(Boolean).sort();
      setQaCategories(cats);
    } catch (err) {
      console.error('Failed to load Q&A categories:', err);
    }
  };
  
  // Load questions when Q&A category changes
  useEffect(() => {
    if (repair.qa_category) {
      loadQuestionsForCategory(repair.qa_category);
    } else {
      setQuestions([]);
    }
  }, [repair.qa_category]);
  
  // Load questions for a category
  const loadQuestionsForCategory = async (categoryName) => {
    setLoadingQuestions(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inspection-qa/questions`);
      const filtered = (response.data || []).filter(q => 
        q.category_name === categoryName && q.question_text
      );
      setQuestions(filtered);
    } catch (err) {
      console.error('Failed to load questions:', err);
      setQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };
  
  // Update selected question data when question changes
  useEffect(() => {
    if (repair.question_id && questions.length > 0) {
      const qData = questions.find(q => q.question_id === repair.question_id);
      setSelectedQuestionData(qData || null);
    } else {
      setSelectedQuestionData(null);
    }
  }, [repair.question_id, questions]);
  
  // Get answer options for the selected question
  const answerOptions = useMemo(() => {
    if (!selectedQuestionData) return [];
    
    const options = [];
    // Main options
    if (selectedQuestionData.options?.length > 0) {
      options.push(...selectedQuestionData.options);
    }
    // Sub options 1
    if (selectedQuestionData.sub_options_1?.length > 0) {
      options.push(...selectedQuestionData.sub_options_1);
    }
    // Sub options 2
    if (selectedQuestionData.sub_options_2?.length > 0) {
      options.push(...selectedQuestionData.sub_options_2);
    }
    // Remove duplicates
    return [...new Set(options)];
  }, [selectedQuestionData]);
  
  // Check if this repair has unsaved price changes or is a new part
  const hasUnsavedChanges = useMemo(() => {
    // New part not in master
    if (!repair.part_id && repair.item) return true;
    // Price was manually overridden
    if (repair.price_source === 'manual_override' || repair.price_source === 'manual') return true;
    // Q&A linking added but not saved (has question and answer but price_source indicates not synced)
    if (repair.question_id && repair.answer_trigger && repair.price_source !== 'synced') return true;
    return false;
  }, [repair]);
  
  // Handle save to master complete
  const handleSaveComplete = (partId) => {
    // Update the repair with the new part_id
    onUpdate(index, {
      ...repair,
      part_id: partId,
      price_source: 'synced'
    });
    onSaveToMaster && onSaveToMaster(repair, index);
  };
  
  // Filter parts based on search
  const filteredParts = useMemo(() => {
    if (!searchQuery) return repairParts;
    const query = searchQuery.toLowerCase();
    return repairParts.filter(p => 
      p.name?.toLowerCase().includes(query) || 
      p.category?.toLowerCase().includes(query)
    );
  }, [repairParts, searchQuery]);
  
  // Get price for a part based on car type and brand
  const getPartPrice = (part, action = 'repair') => {
    if (!part) return { price: 0, labor: 0, total: 0, source: 'manual' };
    
    // Check for brand-specific override first
    const brandOverride = part.brand_overrides?.find(
      bo => bo.brand?.toLowerCase() === vehicleMake?.toLowerCase()
    );
    
    let pricing;
    let source = 'default';
    
    if (brandOverride && brandOverride[carType]) {
      pricing = brandOverride[carType];
      source = `brand:${vehicleMake}`;
    } else if (part[carType]) {
      pricing = part[carType];
      source = `carType:${carType}`;
    } else {
      pricing = part.sedan || {}; // Default to sedan pricing
      source = 'default:sedan';
    }
    
    // Handle different action types
    let price = 0;
    let labor = 0;
    
    switch ((action || 'repair').toLowerCase()) {
      case 'repair':
        // Part cost only (repair price)
        price = pricing.repair_price || 0;
        labor = 0;
        break;
      case 'labor':
        // Labor cost only
        price = 0;
        labor = pricing.repair_labor || 0;
        break;
      case 'both':
        // Both part and labor
        price = pricing.repair_price || 0;
        labor = pricing.repair_labor || 0;
        break;
      case 'replace':
        // Replace costs (for backward compatibility)
        price = pricing.replace_price || 0;
        labor = pricing.replace_labor || 0;
        break;
      case 'inspect_further':
        // No cost, just flag for inspection
        price = 0;
        labor = 0;
        break;
      default:
        price = pricing.repair_price || 0;
        labor = pricing.repair_labor || 0;
    }
    
    return {
      price,
      labor,
      total: price + labor,
      source
    };
  };
  
  // Handle part selection from master list
  const handlePartSelect = (part) => {
    const action = repair.action || 'repair';
    const priceInfo = getPartPrice(part, action);
    
    onUpdate(index, {
      ...repair,
      part_id: part.id,
      item: part.name,
      category: part.category,
      part_number: part.part_number,
      price: priceInfo.price,
      labor: priceInfo.labor,
      estimated_cost: priceInfo.total,
      price_source: priceInfo.source,
      // Store full part data for brand-specific pricing
      part_data: part
    });
    setShowPartSelector(false);
    setSearchQuery('');
  };
  
  // Handle action type change (repair/replace)
  const handleActionChange = (newAction) => {
    if (repair.part_data) {
      const priceInfo = getPartPrice(repair.part_data, newAction);
      onUpdate(index, {
        ...repair,
        action: newAction,
        price: priceInfo.price,
        labor: priceInfo.labor,
        estimated_cost: priceInfo.total,
        price_source: priceInfo.source
      });
    } else {
      onUpdate(index, { ...repair, action: newAction });
    }
  };
  
  // Handle manual price override
  const handlePriceOverride = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const updatedRepair = { ...repair, [field]: numValue };
    
    // Recalculate total
    const price = field === 'price' ? numValue : (repair.price || 0);
    const labor = field === 'labor' ? numValue : (repair.labor || 0);
    updatedRepair.estimated_cost = price + labor;
    updatedRepair.price_source = 'manual_override';
    
    onUpdate(index, updatedRepair);
  };
  
  return (
    <div className="p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
      <div className="flex items-start gap-3">
        {/* Index Badge */}
        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
          repair.action === 'replace' ? 'bg-red-100 text-red-700' :
          repair.type === 'CRITICAL' ? 'bg-red-100 text-red-700' :
          repair.type === 'MAJOR' ? 'bg-orange-100 text-orange-700' :
          'bg-blue-100 text-blue-700'
        }`}>
          {index + 1}
        </div>
        
        <div className="flex-1 space-y-3">
          {/* Row 1: Part Selection & Action Type */}
          <div className="grid grid-cols-12 gap-2">
            {/* Part Name / Selector */}
            <div className="col-span-5 relative">
              <Label className="text-xs text-gray-500 mb-1 block">Part / Item</Label>
              {showPartSelector ? (
                <div className="absolute z-50 top-6 left-0 right-0 bg-white border rounded-lg shadow-xl max-h-72 overflow-hidden">
                  <div className="p-2 border-b sticky top-0 bg-white">
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search parts..."
                      className="h-8 text-sm"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {filteredParts.length > 0 ? (
                      filteredParts.map(part => (
                        <button
                          key={part.id}
                          onClick={() => handlePartSelect(part)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b last:border-b-0"
                        >
                          <div className="font-medium text-sm">{part.name}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span className="bg-gray-100 px-1.5 py-0.5 rounded">{part.category}</span>
                            {part.part_number && <span>#{part.part_number}</span>}
                          </div>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No parts found. Add via Repairs Module.
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t bg-gray-50">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowPartSelector(false); setSearchQuery(''); }}
                      className="w-full text-xs"
                    >
                      <X className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-1">
                  <Input
                    value={repair.item || ''}
                    onChange={(e) => onUpdate(index, { ...repair, item: e.target.value, part_id: null, part_data: null, price_source: 'manual' })}
                    placeholder="Part name or search..."
                    className="h-8 text-sm flex-1"
                    disabled={!canEdit}
                  />
                  {canEdit && repairParts.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPartSelector(true)}
                      className="h-8 px-2"
                      title="Select from parts list"
                    >
                      <Package className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
              {repair.part_id && (
                <span className="text-xs text-green-600 mt-0.5 block">
                  ✓ Synced with Repairs Module
                  {repair.question_id && repair.answer_trigger && ' + Rule linked'}
                </span>
              )}
            </div>
            
            {/* Component (formerly Category) */}
            <div className="col-span-3">
              <Label className="text-xs text-gray-500 mb-1 block">Component</Label>
              <Select 
                value={repair.category || ''} 
                onValueChange={(val) => onUpdate(index, { ...repair, category: val })}
                disabled={!canEdit || repair.part_id}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select component" />
                </SelectTrigger>
                <SelectContent>
                  {partCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Action Type (Repair/Replace) */}
            <div className="col-span-2">
              <Label className="text-xs text-gray-500 mb-1 block">Action</Label>
              <Select 
                value={repair.action || 'repair'} 
                onValueChange={handleActionChange}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACTION_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Priority/Type */}
            <div className="col-span-2">
              <Label className="text-xs text-gray-500 mb-1 block">Priority</Label>
              <Select 
                value={repair.type || 'MINOR'} 
                onValueChange={(val) => onUpdate(index, { ...repair, type: val })}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPAIR_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Row 2: Q&A Linking (Optional) */}
          <div className="grid grid-cols-12 gap-2 items-end border-t pt-3 mt-2">
            <div className="col-span-12 mb-1">
              <span className="text-xs font-medium text-gray-600">Link to Inspection Q&A (Optional - for auto-detection)</span>
            </div>
            
            {/* Q&A Category */}
            <div className="col-span-3">
              <Label className="text-xs text-gray-500 mb-1 block">Q&A Category</Label>
              <Select 
                value={repair.qa_category || ''} 
                onValueChange={(val) => onUpdate(index, { ...repair, qa_category: val, question_id: '', answer_trigger: '' })}
                disabled={!canEdit}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Select Q&A category" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {qaCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Question */}
            <div className="col-span-5">
              <Label className="text-xs text-gray-500 mb-1 block">Question</Label>
              <Select 
                value={repair.question_id || ''} 
                onValueChange={(val) => onUpdate(index, { ...repair, question_id: val, answer_trigger: '' })}
                disabled={!canEdit || !repair.qa_category || loadingQuestions}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={loadingQuestions ? "Loading..." : "Select question"} />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {questions.map((q) => (
                    <SelectItem key={q.question_id} value={q.question_id}>
                      {q.question_text?.substring(0, 60)}{q.question_text?.length > 60 ? '...' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Answer Trigger */}
            <div className="col-span-4">
              <Label className="text-xs text-gray-500 mb-1 block">Answer that triggers repair</Label>
              <Select 
                value={repair.answer_trigger || ''} 
                onValueChange={(val) => onUpdate(index, { ...repair, answer_trigger: val })}
                disabled={!canEdit || !repair.question_id || answerOptions.length === 0}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={answerOptions.length === 0 ? "No options" : "Select answer"} />
                </SelectTrigger>
                <SelectContent>
                  {answerOptions.map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Row 3: Pricing */}
          <div className="grid grid-cols-12 gap-2 items-end">
            {/* Part Price */}
            <div className="col-span-3">
              <Label className="text-xs text-gray-500 mb-1 block">Part Price (₹)</Label>
              <Input
                type="number"
                value={repair.price || ''}
                onChange={(e) => handlePriceOverride('price', e.target.value)}
                placeholder="0"
                className="h-8 text-sm"
                disabled={!canEdit}
              />
            </div>
            
            {/* Labor Cost */}
            <div className="col-span-3">
              <Label className="text-xs text-gray-500 mb-1 block">Labor (₹)</Label>
              <Input
                type="number"
                value={repair.labor || ''}
                onChange={(e) => handlePriceOverride('labor', e.target.value)}
                placeholder="0"
                className="h-8 text-sm"
                disabled={!canEdit}
              />
            </div>
            
            {/* Total Cost */}
            <div className="col-span-3">
              <Label className="text-xs text-gray-500 mb-1 block">Total (₹)</Label>
              <div className="h-8 px-3 flex items-center bg-gray-100 rounded-md text-sm font-bold text-gray-900">
                ₹{(repair.estimated_cost || 0).toLocaleString()}
              </div>
            </div>
            
            {/* Actions */}
            <div className="col-span-3 flex items-center gap-1 justify-end">
              {repair.price_source && repair.price_source !== 'manual' && repair.price_source !== 'manual_override' && (
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  {repair.price_source.includes('brand') ? '🏷️ Brand' : '📋 Default'}
                </span>
              )}
              {/* Save to Master Button - shows when there are unsaved changes */}
              {canEdit && hasUnsavedChanges && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSaveToMasterModal(true)}
                  className="h-8 px-2 text-xs text-green-600 border-green-300 hover:bg-green-50"
                  title="Save this part/pricing to master data"
                >
                  <Save className="h-3 w-3 mr-1" />
                  Save to Master
                </Button>
              )}
              {canEdit && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onRemove(index)} 
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Row 3: Category & Notes (if part is from module) */}
          {(repair.category || repair.part_number) && (
            <div className="flex items-center gap-3 text-xs text-gray-500">
              {repair.category && (
                <span className="bg-gray-100 px-2 py-0.5 rounded">{repair.category}</span>
              )}
              {repair.part_number && (
                <span>Part #: {repair.part_number}</span>
              )}
              {repair.price_source === 'manual_override' && (
                <span className="text-orange-600 bg-orange-50 px-2 py-0.5 rounded">
                  ⚠️ Custom pricing (not saved to master)
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Save to Master Modal */}
      <SaveToMasterModal
        isOpen={showSaveToMasterModal}
        onClose={() => setShowSaveToMasterModal(false)}
        repair={repair}
        repairParts={repairParts}
        vehicleMake={vehicleMake}
        carType={carType}
        onSaveComplete={handleSaveComplete}
        countryId={countryId}
      />
    </div>
  );
};



export default RepairItem;
